import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, BoxProps, Button, Typography } from '@mui/material';
import { Stop } from '@app/shared/models/tour.type';
import { Seperator, TourChip } from '@app/shared/components';
import { useTranslation } from 'react-i18next';
import { StopDetails } from 'src/features/map/stop-details/StopDetails';
import { RateUsModal } from 'src/features/map/stop-details/RateUsModal';
import { useSetRecoilState, useRecoilValue, useRecoilState, useRecoilCallback } from 'recoil';
import { atom_scrollContainerRef } from '@app/shared/state/scroll-target.atom';
import { atom_navigationHelperState, atom_showModalContent, atom_modalType } from '@app/shared/state/show-content-modal.atom';
import { atom_isDesktop, atom_tourState } from '@app/shared/state/tour.atom';
import { atom_listModeState } from '@app/shared/state/list-mode.atom';
import { atom_openedStopState } from '@app/shared/state/opened-stop.atom';
import { atom_savedStopState } from '@app/shared/state/saved-stop-state.atom';
import { useNotificationTrigger } from '@app/shared/hooks/useNotificationTrigger';
import { passiveCollector, isWithinRadius } from '@app/shared/services/passive-data/passive-data-collector';
import { atom_geolocationState } from '@app/shared/state/geolocation.atom';
import { useRenderTracker } from '@app/shared/debug';
import { safePushState, safeHistoryBack } from '@app/shared/utils/safe-history';

export type BigStopPageProps = {
	stop: Stop;
	children?: React.ReactNode;
};

export const BigStopPage = React.memo(
	React.forwardRef<HTMLDivElement, BigStopPageProps & BoxProps>(function BigStopPage(
		_p,
		ref
	) {
		useRenderTracker('BigStopPage');
		const { t, i18n } = useTranslation();
		const [showReviewModal, setShowReviewModal] = useState(false);
		const isRTL = i18n.language === 'he';
		const setScrollContainerRef = useSetRecoilState(atom_scrollContainerRef);
		const setNavState = useSetRecoilState(atom_navigationHelperState);
		const isDesktop = useRecoilValue(atom_isDesktop);
		const setListMode = useSetRecoilState(atom_listModeState);
		const tourState = useRecoilValue(atom_tourState);
		const setOpenedStopState = useSetRecoilState(atom_openedStopState);
		const setSavedStopState = useSetRecoilState(atom_savedStopState);
		const setState_showModal = useSetRecoilState(atom_showModalContent);
		const [modalType, setState_modalType] = useRecoilState(atom_modalType);
		const { fireTrigger } = useNotificationTrigger();
		const getGeolocation = useRecoilCallback(({ snapshot }) => () => {
			return snapshot.getLoadable(atom_geolocationState).getValue();
		});

		// Track modalType at mount time so popstate closure knows the entry source
		const enteredFromRef = useRef(modalType);

		// Handle mobile back button - push history state on mount, close on popstate
		const closedByBackRef = useRef(false);
		useEffect(() => {
			safePushState({ bigStopPage: true });

			// Save bigStop state to localStorage so the app can restore position
			if (tourState?.id && _p.stop?.id) {
				localStorage.setItem(`stop_modal_state_${tourState.id}`, JSON.stringify({
					stopId: _p.stop.id,
					modalType: 'bigStop',
					timestamp: new Date().toISOString()
				}));
			}

			const onPopState = () => {
				closedByBackRef.current = true;
				if (_p.stop?.id) {
					const geo = getGeolocation();
					passiveCollector.trackStopClosed(_p.stop.id, isWithinRadius(geo.latitude, geo.longitude, _p.stop));
				}
				setOpenedStopState(undefined);
				// Always go back to the map
				if (tourState?.id) {
					localStorage.removeItem(`stop_modal_state_${tourState.id}`);
				}
				setState_showModal(false);
				setState_modalType(null);
			};

			window.addEventListener('popstate', onPopState);
			return () => {
				window.removeEventListener('popstate', onPopState);
				if (!closedByBackRef.current) {
					safeHistoryBack();
				}
			};
		}, [_p.stop?.id, setSavedStopState, setOpenedStopState]);

		// Handle closing the stop page (X button)
		const handleClose = useCallback(() => {
			if (_p.stop?.id) {
				const geo = getGeolocation();
				passiveCollector.trackStopClosed(_p.stop.id, isWithinRadius(geo.latitude, geo.longitude, _p.stop));
				fireTrigger('action', { stopId: _p.stop.id });
				fireTrigger('locationAndAction', { stopId: _p.stop.id });
			}

			setOpenedStopState(undefined);

			// Always go back to map
			if (tourState?.id) {
				localStorage.removeItem(`stop_modal_state_${tourState.id}`);
			}
			setState_showModal(false);
			setState_modalType(null);
		}, [setOpenedStopState, setState_showModal, setState_modalType, fireTrigger, _p.stop?.id, tourState?.id]);
		// Callback ref that sets the Recoil atom directly
		const scrollContainerRef = useCallback((node: HTMLDivElement | null) => {
			// Set the forwarded ref if it exists
			if (ref) {
				if (typeof ref === 'function') {
					// callback refs
					(ref as (instance: HTMLDivElement | null) => void)(node);
				} else if (typeof ref === 'object') {
					// Ref object
					(ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
				}
			}

			// Set the Recoil atom with this node's scrollable parent
			if (node) {
				let element = node.parentElement;
				while (element && element !== document.body) {
					const overflowY = window.getComputedStyle(element).overflowY;
					if (overflowY === 'auto' || overflowY === 'scroll') {
						setScrollContainerRef(element);
						break;
					}
					element = element.parentElement;
				}

				// Focus for keyboard scrolling
				node.focus();
			}
		}, [ref, setScrollContainerRef]);


		return (
			<Box
				ref={scrollContainerRef}
				dir={isRTL ? 'rtl' : 'ltr'}
				sx={{
					width: '100%',
					minHeight: '100vh',
					maxWidth: 1200,
					margin: '0 auto',
					px: { xs: 0, md: 3 },
					pb: 3,
					outline: 'none',
					'&:focus': {
						outline: 'none'
					}
				}}
				component="section"
				role="region"
				aria-label={t('on_tour.stop.info') as string}
				tabIndex={0}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						padding: 2,
						direction: isRTL ? 'rtl' : 'ltr',
					}}
				>
					{/* <Button
						onClick={(e) => {
							if (isDesktop) {
								// Desktop: use popover navigation
								setNavState({
									anchorEl: e.currentTarget,
									offset: { top: 0, x: -20 },
									usePopover: true
								});
							} else {
								// Mobile: use resizable list mode navigation
								setListMode('mobileNavigation');
							}
						}}
						className="p-0 w-10 h-10 min-w-min"
						aria-label={t('on_tour.stop.open_navigation') as string}
						sx={{
							minWidth: '40px',
							width: '40px',
							height: '40px',
							padding: 0,
							boxShadow: '0 2px 6px rgba(0,0,0,0.16)'
						}}
					>
						<i
							className="fa-solid fa-diamond-turn-right"
							aria-hidden="true"
							style={{ fontSize: '20px' }}
						/>
					</Button> */}
					<Box
						component="button"
						onClick={handleClose}
						aria-label={t('on_tour.go_back_tour') as string}
						sx={{
							width: 36,
							height: 36,
							borderRadius: '50%',
							background: '#F6F6F9',
							boxShadow: '0 2px 6px rgba(0,0,0,0.16)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							cursor: 'pointer',
							border: 'none',
							padding: 0,
							transition: 'transform 0.2s ease',
							'&:hover': {
								transform: 'scale(0.9)',
							},
							'&:active': {
								transform: 'scale(0.85)',
							},
						}}
					>
						<svg
							width={20}
							height={20}
							viewBox="0 0 24 24"
							fill="#333"
							aria-hidden="true"
						>
							<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
						</svg>
					</Box>
					<Box className="flex flex-wrap items-center max-w-[75%] justify-center">
						{_p.stop.tags?.map((tag, index) => (
							<Typography
								key={`tag-${tag?.text || index}`}
								variant="body2"
								textTransform="capitalize"
							>
								{tag.text}&nbsp;&nbsp;
							</Typography>
						))}
					</Box>
					{/* <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}> */}
					<Button
						onClick={(e) => {
							if (isDesktop) {
								// Desktop: use popover navigation
								setNavState({
									anchorEl: e.currentTarget,
									offset: { top: 0, x: -20 },
									usePopover: true
								});
							} else {
								// Mobile: use resizable list mode navigation
								setListMode('mobileNavigation');
							}
						}}
						className="p-0 w-10 h-10 min-w-min"
						aria-label={t('on_tour.stop.open_navigation') as string}
						sx={{
							minWidth: '40px',
							width: '40px',
							height: '40px',
							padding: 0,
							boxShadow: '0 2px 6px rgba(0,0,0,0.16)'
						}}
					>
						<i
							className="fa-solid fa-diamond-turn-right"
							aria-hidden="true"
							style={{ fontSize: '20px' }}
						/>
					</Button>
					{/* <NavigationOptions stop={_p.stop} omitWaze={true} /> */}
					{/* </Box> */}
				</Box>

				<Box sx={{ overflow: 'hidden', minWidth: 0 }}>
					<Box className="px-4">
						<Typography variant="h1" className="mb-2" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
							{_p.stop.s_title}
						</Typography>
						{_p.stop?.hours_range?.min && _p?.stop?.hours_range?.max && <TourChip icon_type="solid" icon_name="clock" sx={{ marginBottom: 1 }}>
							{_p.stop.hours_range.min} - {_p.stop.hours_range.max}
						</TourChip>}
						{_p.stop.s_smallDesc && (
							<Typography style={{ wordBreak: 'break-word' }} variant="body2" sx={{ textAlign: isRTL ? 'right' : 'left' }}>
								{_p.stop.s_smallDesc}
							</Typography>
						)}
						<Seperator />
					</Box>
					<StopDetails />
					<Box sx={{ minHeight: '5vh', display: { md: 'none' } }}></Box>
				</Box>

				<Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', alignItems: 'center', gap: 1, px: 4, mb: '10px' }}>
					<Typography variant="body2">{t('on_tour.review.ask')}</Typography>
					<Button
						variant="outlined"
						onClick={() => setShowReviewModal(true)}
						aria-label={t('on_tour.review.write') as string}
						sx={{ width: '80%', borderColor: 'currentColor' }}
					>
						{t('on_tour.review.write')}
					</Button>
				</Box>

				<RateUsModal showModal={showReviewModal} onClose={() => setShowReviewModal(false)} />
			</Box>
		);
	})
);
